/**
 * Converts a native File object into a Base64 string for the backend.
 * @param {File} file - The file to convert
 * @returns {Promise<string|null>} - The Base64 string (or null if no file)
 */
export const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    if (!file || !(file instanceof File)) {
      resolve(file); // Return as-is if it's already null or a string
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Converts a Base64 string from the backend into a native File object.
 * @param {string} base64String - The base64 string from the API
 * @param {string} filename - Fallback filename
 * @returns {File|null} - A native File object that OpImageUploader can read
 */
export const convertBase64ToFile = (base64String, filename = 'uploaded-image') => {
  if (!base64String || typeof base64String !== 'string') return null;

  try {
    // 1. Separate the MIME type from the raw data
    const isDataURI = base64String.includes(',');
    const rawData = isDataURI ? base64String.split(',')[1] : base64String;
    
    // Extract mime type (default to png if not provided by backend)
    const mimeMatch = base64String.match(/data:(.*?);base64/);
    const mimeType = mimeMatch ? mimeMatch[1] : 'image/png';
    
    // 2. Decode the base64 string
    const byteString = atob(rawData);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }

    // 3. Create and return the File object
    const extension = mimeType.split('/')[1] || 'png';
    const finalFileName = filename.includes(extension)
      ? filename
      : `${filename}${extension}`;
    return new File([uint8Array], finalFileName, { type: mimeType });
  } catch (error) {
    console.error('Failed to convert base64 to File', error);
    return null;
  }
};